import React, { useEffect, useMemo, useRef, useState } from "react";

type Props = {
    /** Totale duur van 1 loop (ms) */
    durationMs?: number;

    /** Pauzeer */
    paused?: boolean;

    /** Render size (px). Laat leeg voor responsive */
    width?: number;
    height?: number;

    /** Basiskleur (lijnen/tekst) */
    baseColor?: string;

    /** Accent/goud */
    goldColor?: string;

    /** Glow sterkte */
    glowBlur?: number;

    /** Hoelang de kaart in elke kolom “hangt” (ms). Default 2000 */
    holdMsPerColumn?: number;

    /** Hoelang de verschuiving duurt tussen kolommen (ms). Default 700 */
    moveMs?: number;

    /** Extra hoogte van het bord (viewBox units). Default 40 */
    boardHeight?: number;
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}
function clamp01(n: number) {
    return clamp(n, 0, 1);
}
function lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
}
function easeInOut(t: number) {
    return t * t * (3 - 2 * t);
}

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!mq) return;
        const onChange = () => setReduced(!!mq.matches);
        onChange();
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return reduced;
}

/**
 * DevelopScene — Kanban Micro-Flow
 * - Kaart hangt ~Xs per kolom, verschuift rustig
 * - Check verschijnt in Done (gold glow)
 * - Loop zonder fade/reset (kaart springt terug naar To do aan einde)
 */
export default function DevelopScene({
    durationMs = 12000,
    paused = false,

    width,
    height,

    baseColor = "#8A8A8A",
    goldColor = "rgb(var(--primary))",
    glowBlur = 0.85,

    holdMsPerColumn = 4000,
    moveMs = 700,

    boardHeight = 75,
}: Props) {
    const reduced = usePrefersReducedMotion();

    const ids = useMemo(() => {
        const s = Math.random().toString(16).slice(2);
        return {
            glow: `kb-glow-${s}`,
            soft: `kb-soft-${s}`,
            cardClip: `kb-card-clip-${s}`,
        };
    }, []);

    const rafRef = useRef<number | null>(null);
    const startRef = useRef<number | null>(null);
    const [tMs, setTMs] = useState(0);

    const safeHold = clamp(holdMsPerColumn ?? 2000, 0, 60000);
    const safeMove = clamp(moveMs ?? 700, 50, 60000);

    // Timeline:
    // hold todo -> move -> hold doing -> move -> hold done
    const H1 = reduced ? 1 : safeHold;
    const M1 = reduced ? 1 : safeMove;
    const H2 = reduced ? 1 : safeHold;
    const M2 = reduced ? 1 : safeMove;
    const H3 = reduced ? 1 : safeHold;

    const loopLen = Math.max(1, H1 + M1 + H2 + M2 + H3);
    const safeDuration =
        Number.isFinite(durationMs) && durationMs > 0 ? durationMs : loopLen;

    // We gebruiken loopLen voor animatie, durationMs bepaalt alleen hoe vaak je wilt herstarten
    // Als durationMs groter is, loopt het patroon gewoon meerdere keren binnen die duration.
    const effectiveLen = loopLen;

    useEffect(() => {
        if (reduced) {
            setTMs(effectiveLen);
            return;
        }

        const tick = (now: number) => {
            if (startRef.current === null) startRef.current = now;

            if (!paused) {
                const elapsed = now - startRef.current;
                setTMs(elapsed % effectiveLen);
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            startRef.current = null;
        };
    }, [paused, effectiveLen, reduced]);

    const A = 0;
    const B = A + H1;
    const C = B + M1;
    const D = C + H2;
    const E = D + M2;
    const F = E + H3;

    const inRange = (x: number, a: number, b: number) => x >= a && x < b;

    let cardStage: "todo" | "move1" | "doing" | "move2" | "done" = "todo";
    if (inRange(tMs, A, B)) cardStage = "todo";
    else if (inRange(tMs, B, C)) cardStage = "move1";
    else if (inRange(tMs, C, D)) cardStage = "doing";
    else if (inRange(tMs, D, E)) cardStage = "move2";
    else cardStage = "done";

    const move1P = cardStage === "move1" ? easeInOut(clamp01((tMs - B) / M1)) : 0;
    const move2P = cardStage === "move2" ? easeInOut(clamp01((tMs - D) / M2)) : 0;

    // Board geometry (viewBox 0..100 x 0..70) — hoger bord
    const VB_W = 100;
    const VB_H = 70;

    const boardX = 4;
    const boardY = 6;
    const colW = 26;
    const colH = clamp(boardHeight, 40, 56);
    const gap = 3;

    const col1X = boardX;
    const col2X = boardX + colW + gap;
    const col3X = boardX + (colW + gap) * 2;

    const cardW = colW - 6;
    const cardH = 12; // iets groter
    const cardY = boardY + 12; // iets lager in kolom

    const x1 = col1X + 3;
    const x2 = col2X + 3;
    const x3 = col3X + 3;

    let cardX = x1;
    if (cardStage === "todo") cardX = x1;
    if (cardStage === "move1") cardX = lerp(x1, x2, move1P);
    if (cardStage === "doing") cardX = x2;
    if (cardStage === "move2") cardX = lerp(x2, x3, move2P);
    if (cardStage === "done") cardX = x3;

    const checkOn = cardStage === "done";
    const checkPulse = reduced
        ? 1
        : 0.6 + 0.4 * Math.sin(((tMs - E) / Math.max(1, H3)) * Math.PI * 2);

    const svgSizing =
        typeof width === "number" && typeof height === "number"
            ? { width, height }
            : { width: "100%", height: "100%" as const };

    return (
        <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block" }}
            role="img"
            aria-label="Ontwikkelen: kanban micro-flow"
            {...svgSizing}
        >
            <defs>
                <filter id={ids.glow} x="-160%" y="-160%" width="420%" height="420%">
                    <feGaussianBlur stdDeviation={glowBlur} result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>

                <filter id={ids.soft} x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur stdDeviation="0.45" />
                </filter>

                <clipPath id={ids.cardClip}>
                    <rect x={cardX} y={cardY} width={cardW} height={cardH} rx="2.8" />
                </clipPath>

                <style>{`
            .col {
                stroke: ${baseColor};
                fill: none;
                vector-effect: non-scaling-stroke;
                stroke-linecap: round;
                stroke-linejoin: round;
            }

            /* Bord duidelijker */
            .panel {
                stroke-width: 2;
                opacity: 0.85;
            }

            .label {
                font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
                font-size: 3.35px;
                fill: ${baseColor};
                opacity: 0.80;
            }

            .panel { stroke-width: 2.1; opacity: 0.95; }
            .label {
                font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
                font-size: 4.6px;
                font-weight: 600;
                fill: ${baseColor};
                opacity: 0.95;
            }
            .muted { opacity: 0.80; stroke-width: 1.15; }
            .card-fill {stroke: ${goldColor}; fill: ${baseColor}; opacity: 0.3; }

            .check {
                stroke: ${goldColor};
                stroke-width: 1.85;
                fill: none;
                opacity: 0.92;
            }
        `}</style>
            </defs>

            {/* Board outline */}
            <rect
                x={boardX - 2}
                y={boardY - 2}
                width={colW * 3 + gap * 2 + 4}
                height={colH + 4}
                rx="4.2"
                className="col panel"
                opacity={0.34}
            />

            {/* Columns */}
            <g>
                <rect x={col1X} y={boardY} width={colW} height={colH} rx="3.2" className="col panel" />
                <rect x={col2X} y={boardY} width={colW} height={colH} rx="3.2" className="col panel" />
                <rect x={col3X} y={boardY} width={colW} height={colH} rx="3.2" className="col panel" />
            </g>

            {/* Column labels */}
            <text x={col1X + 3} y={boardY + 6.2} className="label">
                To do
            </text>
            <text x={col2X + 3} y={boardY + 6.2} className="label">
                Doing
            </text>
            <text x={col3X + 3} y={boardY + 6.2} className="label">
                Done
            </text>

            {/* Acceptance lines */}
            <g className="col muted" clipPath={`url(#${ids.cardClip})`}>

                {/* kleine padding binnen de card */}
                {(() => {
                    const padX = 2.4;
                    const padY = 3.8;
                    const lineGap = 2.8;

                    const l1x = cardX + padX;
                    const r1 = cardX + cardW - padX;

                    const y1 = cardY + padY;
                    const y2 = y1 + lineGap;
                    const y3 = y2 + lineGap;

                    return (
                        <>
                            <path d={`M ${l1x} ${y1} H ${r1}`} />
                            <path d={`M ${l1x} ${y2} H ${r1 - 5.5}`} />
                            <path d={`M ${l1x} ${y3} H ${r1 - 9}`} />
                        </>
                    );
                })()}
            </g>

            {/* Card */}
            <g>
                <rect x={cardX} y={cardY} width={cardW} height={cardH} rx="2.8" className="col card" />
                <rect x={cardX} y={cardY} width={cardW} height={cardH} rx="2.8" className="card-fill" />

                {/* Acceptance lines — IN de card */}
                <g className="col muted" clipPath={`url(#${ids.cardClip})`}>
                    {(() => {
                        const padX = 2.4;
                        const padY = 3.8;
                        const lineGap = 2.8;

                        const l1x = cardX + padX;
                        const r1 = cardX + cardW - padX;

                        const y1 = cardY + padY;
                        const y2 = y1 + lineGap;
                        const y3 = y2 + lineGap;

                        return (
                            <>
                                <path d={`M ${l1x} ${y1} H ${r1}`} />
                                <path d={`M ${l1x} ${y2} H ${r1 - 5.5}`} />
                                <path d={`M ${l1x} ${y3} H ${r1 - 9}`} />
                            </>
                        );
                    })()}
                </g>
            </g>

            {/* Done check (gold glow) */}
            {checkOn && (
                <g opacity={0.55 + 0.45 * checkPulse} filter={`url(#${ids.glow})`}>
                    <path
                        className="check"
                        d={`M ${col3X + 6} ${boardY + colH - 9} L ${col3X + 10.5} ${boardY + colH - 4.5} L ${col3X + 19} ${boardY + colH - 15}`}
                    />
                </g>
            )}
        </svg>
    );
}
